import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Bot, ClipboardCopy, Loader2 } from 'lucide-react';
import { hora } from '../chat/BurbujaChat';
import { ChipClasificacion, ChipsEstado, DetalleEstado, FOCO, comoObjeto } from './bot/comun';

/**
 * El bot dentro de la conversación de la bandeja (1/11/2026):
 *  - sus BORRADORES (modo prueba, o «¿Qué contestaría el bot?»): del lado de
 *    la clínica pero con borde discontinuo y «Borrador del bot · no
 *    enviado», con lo que ha entendido plegado y «Copiar al cuadro de texto»;
 *  - el intento que salió mal, en una línea discreta con el motivo;
 *  - «El bot está pensando…» mientras prepara una respuesta;
 *  - y, cuando el bot ya envía, una ficha pequeña con lo último que apuntó.
 */

const MARCADOR_ENLACE = /\{\{\s*ENLACE_CITA\s*\}\}/g;
export const AVISO_SIN_ENLACE = 'Falta el teléfono de cita en WhatsApp → Bot: el enlace no está puesto';

/** ¿Lleva el texto el marcador del enlace de cita sin sustituir? */
export function tieneMarcadorEnlace(texto) {
  return /\{\{\s*ENLACE_CITA\s*\}\}/.test(String(texto || ''));
}

/** El texto sin el marcador (y sin los espacios que deja). */
export function quitarMarcadorEnlace(texto) {
  return String(texto || '').replace(MARCADOR_ENLACE, '').replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+\n/g, '\n').trim();
}

function tiempo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/**
 * Mete los borradores entre los mensajes, por su hora: cada uno va detrás del
 * último mensaje anterior a él. Los que no tienen hora, al final.
 * `filas` es lo que devuelve conRachas (cada una con su `m`).
 */
export function intercalarBorradores(filas, borradores) {
  const lista = (Array.isArray(borradores) ? borradores : []).filter((b) => b && typeof b === 'object');
  const salida = [];
  let j = 0;
  (filas || []).forEach((f) => {
    const tm = tiempo(f.m?.created_at);
    while (j < lista.length && tm !== null && tiempo(lista[j].at) !== null && tiempo(lista[j].at) < tm) {
      salida.push({ tipo: 'borrador', b: lista[j] });
      j += 1;
    }
    salida.push({ tipo: 'mensaje', ...f });
  });
  while (j < lista.length) {
    salida.push({ tipo: 'borrador', b: lista[j] });
    j += 1;
  }
  return salida;
}

function motivoDe(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  return error.motivo || error.texto || 'sin motivo';
}

/** Un intento del bot que no salió: una línea, sin alarmar. */
export function LineaBorradorFallido({ b }) {
  return (
    <div className="flex justify-center my-1.5" data-testid="wa-borrador-error">
      <span className="max-w-[90%] text-center text-[11.5px] leading-snug rounded-md px-2 py-0.5 bg-white/80 text-[#54656F] dark:bg-[#182229] dark:text-[#8696A0]">
        <Bot className="w-3 h-3 inline -mt-0.5 mr-1" aria-hidden="true" />
        El bot no pudo preparar un borrador{b.at ? ` (${hora(b.at)})` : ''}: {motivoDe(b.error)}
      </span>
    </div>
  );
}

/** Un borrador: una burbuja discontinua por mensaje, como se enviarían. */
export function BurbujaBorrador({ b, onCopiar }) {
  const mensajes = (Array.isArray(b.mensajes) ? b.mensajes : []).filter((t) => t !== null && t !== undefined);
  const estado = comoObjeto(b.estado);
  const conEstado = Object.keys(estado).length > 0;
  return (
    <div className="flex flex-col items-end gap-0.5 mt-2" data-testid="wa-borrador" data-id={b.id}>
      {mensajes.map((t, i) => (
        <div key={`${i}-${t}`}
          className="relative max-w-[85%] sm:max-w-[70%] rounded-lg border-2 border-dashed border-sky-500/70 bg-white/75 dark:bg-[#0B141A]/70 px-2.5 pt-1 pb-1.5 text-[14.5px] leading-[1.35] text-[#111B21] dark:text-[#E9EDEF]"
          data-testid="wa-borrador-mensaje">
          {i === 0 ? (
            <div className="text-[12px] font-medium leading-5 text-sky-800 dark:text-sky-300 flex items-center gap-1"
              data-testid="wa-borrador-etiqueta">
              <Bot className="w-3.5 h-3.5" aria-hidden="true" />Borrador del bot · no enviado
            </div>
          ) : null}
          <div className="whitespace-pre-wrap break-words">{t}</div>
        </div>
      ))}
      <div className="max-w-[85%] sm:max-w-[70%] w-full flex flex-col items-end gap-1">
        {mensajes.some(tieneMarcadorEnlace) ? (
          <p className="text-[11.5px] text-amber-900 bg-amber-50/90 rounded px-2 py-0.5 text-right" data-testid="wa-borrador-sin-enlace">
            {AVISO_SIN_ENLACE}.
          </p>
        ) : null}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="text-[11px] text-[#54656F] dark:text-[#8696A0] tabular-nums">
            {[b.at ? hora(b.at) : '', b.version_prompt ? `instrucciones v${b.version_prompt}` : ''].filter(Boolean).join(' · ')}
          </span>
          {mensajes.length ? (
            <Button type="button" size="sm" variant="outline" className="h-7 bg-white/80 dark:bg-transparent"
              onClick={() => onCopiar(b)} data-testid="wa-borrador-copiar">
              <ClipboardCopy className="w-3.5 h-3.5" aria-hidden="true" /> Copiar al cuadro de texto
            </Button>
          ) : null}
        </div>
        {conEstado ? (
          <details className="w-full sm:w-auto max-w-full rounded-md bg-white/80 dark:bg-[#111B21]/80 px-2 py-1 text-xs"
            data-testid="wa-borrador-estado">
            <summary className={`cursor-pointer select-none flex items-center gap-1.5 rounded ${FOCO}`}>
              <span className="text-[#54656F] dark:text-[#AEBAC1]">Lo que ha entendido el bot</span>
              <ChipClasificacion valor={estado.clasificacion} />
            </summary>
            <div className="pt-1 space-y-1">
              <ChipsEstado estado={estado} />
              <DetalleEstado estado={estado} />
            </div>
          </details>
        ) : null}
      </div>
    </div>
  );
}

export function BotPensando() {
  return (
    <div className="flex justify-end mt-2" data-testid="wa-bot-pensando" role="status">
      <span className="inline-flex items-center gap-1.5 rounded-lg border-2 border-dashed border-sky-500/50 bg-white/70 dark:bg-[#0B141A]/70 px-2.5 py-1 text-[12.5px] text-sky-900 dark:text-sky-200">
        <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />El bot está pensando…
      </span>
    </div>
  );
}

/** Lo último que apuntó el bot en esta conversación (cuando ya envía). */
export function FichaEstadoBot({ estado, at }) {
  // En el móvil el resumen va en una línea (el hilo es lo que importa); se
  // despliega pulsándolo.
  const [entero, setEntero] = useState(false);
  const e = comoObjeto(estado);
  if (!Object.keys(e).length) return null;
  return (
    <div className="px-3 md:px-4 py-1.5 border-b border-border bg-sky-50/70 dark:bg-sky-950/20 text-xs shrink-0 min-w-0"
      data-testid="wa-ficha-bot">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1 font-semibold text-sky-900 dark:text-sky-200">
          <Bot className="w-3.5 h-3.5" aria-hidden="true" /> Según el bot
        </span>
        <ChipClasificacion valor={e.clasificacion} />
        {e.tratamiento_recomendado ? (
          <span data-testid="wa-ficha-bot-tratamiento"><span className="text-muted-foreground">Tratamiento:</span> {e.tratamiento_recomendado}</span>
        ) : null}
        {e.plazo ? (
          <span data-testid="wa-ficha-bot-plazo"><span className="text-muted-foreground">Plazo:</span> {e.plazo}</span>
        ) : null}
        {at ? <span className="text-muted-foreground ml-auto">{hora(at)}</span> : null}
      </div>
      {e.resumen_para_equipo ? (
        <button type="button" onClick={() => setEntero((v) => !v)} aria-expanded={entero}
          title={entero ? 'Pulsa para recogerlo' : 'Pulsa para leerlo entero'}
          className={`mt-0.5 w-full text-left text-[#13231E] dark:text-[#E9EDEF] rounded ${FOCO} ${
            entero ? 'whitespace-pre-wrap' : 'line-clamp-1 sm:line-clamp-2'}`}
          data-testid="wa-ficha-bot-resumen">
          {e.resumen_para_equipo}
        </button>
      ) : null}
    </div>
  );
}
